import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputField from "components/FormFields/InputField";
import Button from "components/Buttons/Button";
import Render from "components/Common/Render";

interface IResetPasswordFormProps {
  onSubmit: (password: string) => void;
  isLoading: boolean;
  onBackToLogin?: () => void;
}

const validationSchema = Yup.object({
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 alphanumeric")
    .matches(/[a-zA-Z]/, "Password must contain at least 1 letter")
    .matches(/[0-9]/, "Password must contain at least 1 number")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Please confirm your password"),
});

const ResetPasswordForm: React.FC<IResetPasswordFormProps> = ({
  onSubmit,
  isLoading,
  onBackToLogin,
}) => {
  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      onSubmit(values.newPassword);
    },
  });

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col gap-6 mb-8">
        <h1 className="font-poppins font-semibold text-[28px] md:text-[32px] leading-[100%] tracking-[0] text-gray-900">
          Set a password
        </h1>

        <p className="font-poppins font-normal text-[14px] md:text-[16px] leading-[100%] tracking-[0] text-gray-600">
          Your previous password has been reset. Please set a new password for
          your account.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={formik.handleSubmit} className="space-y-6 w-full">
        {/* New Password */}
        <InputField
          label="Enter new password"
          type="password"
          value={formik.values.newPassword}
          onChange={(e) => formik.setFieldValue("newPassword", e.target.value)}
          error={formik.touched.newPassword && formik.errors.newPassword}
          customClass="h-[42px] px-4"
          showPasswordToggle
        />

        {/* Confirm Password */}
        <InputField
          label="Confirm new password"
          type="password"
          value={formik.values.confirmPassword}
          onChange={(e) =>
            formik.setFieldValue("confirmPassword", e.target.value)
          }
          error={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
          customClass="h-[42px] px-4"
          showPasswordToggle
        />

        {/* Buttons */}
        <div className="flex flex-col md:flex-row gap-2 pt-3">
          <Button
            type="submit"
            label={isLoading ? "Submitting..." : "Submit"}
            disabled={!formik.isValid || isLoading || !formik.dirty}
            loading={isLoading}
            variant="primary"
            customClass="!mb-0 !me-0 w-full md:w-[240px] order-1 md:order-2"
          />

          <Render when={!!onBackToLogin}>
            <Button
              type="button"
              label="Back to login"
              onClick={onBackToLogin}
              variant="secondary"
              customClass="!mb-0 !me-0 w-full md:w-[240px] order-2 md:order-1"
            />
          </Render>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordForm;
